import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Box, Grid, Link } from '@mui/material'
import i18n from 'simple-react-i18n'
import { isNil, orderBy } from 'lodash'
import { CardTable } from '../../../components/datatable/NewTable'
import ConfirmModal from '../../../components/modal/ConfirmModal'
import { getFullDate } from '../../../utils/DateUtil'
import { mainBlue } from '../../../utils/constants/ColorTheme'
import RpqsAction from '../../actions/RpqsAction'
import { formatThousands, getRpqsCollectivityDocumentsPath, getRpqsDocumentsPath, RPQS_DOCUMENTS_ROOT } from '../../utils/RPQSUtils'

const PICTURE_REGEX = /\.(jpe?g|png|gif|bmp|svg|webp)$/i
const getHeaders = () => [
    { key: 'preview', value: i18n.photo, noSort: true },
    { key: 'name', value: i18n.name, noSort: true },
    { key: 'managementEntity', value: i18n.managementEntity, noSort: true },
    { key: 'size', value: i18n.size, noSort: true },
    { key: 'updateDate', value: i18n.updateDate, noSort: true },
]

const getRelativePath = url => url.slice(url.indexOf(RPQS_DOCUMENTS_ROOT))

// Sous-répertoire éventuel sous celui de la collectivité = id de l'entité de gestion.
const getManagementUnitName = (url, collectivityPath, managementUnits) => {
    const subDir = getRelativePath(url).replace(collectivityPath, '').split('/').slice(0, -1)[0]
    if (isNil(subDir)) return i18n.rpqsGlobalEvent
    return managementUnits.find(m => m.id === parseInt(subDir))?.name ?? subDir
}

const readAsDataURL = file => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
})

const RpqsDocumentsPanel = ({
    year,
    collectivityId,
    egId,
}) => {
    const dispatch = useDispatch()
    const inputRef = useRef(null)

    const {
        managementUnits,
    } = useSelector(store => ({
        managementUnits: store.RpqsReducer.managementUnits,
    }), shallowEqual)

    const [documents, setDocuments] = useState([])
    const [documentToDelete, setDocumentToDelete] = useState()

    const collectivityPath = getRpqsCollectivityDocumentsPath(year, collectivityId)
    const dirPath = getRpqsDocumentsPath(year, collectivityId, egId)

    const refreshDocuments = useCallback(() => {
        dispatch(RpqsAction.fetchDocuments(dirPath)).then(res => setDocuments(res ?? []))
    }, [dispatch, dirPath])

    useEffect(() => {
        dispatch(RpqsAction.fetchAllManagementUnits())
    }, [dispatch])

    useEffect(() => {
        refreshDocuments()
    }, [refreshDocuments])

    const onUpload = e => {
        const files = [...e.target.files]
        Promise.all(files.map(file => readAsDataURL(file).then(content => dispatch(RpqsAction.uploadDocument(dirPath, file.name, content)))))
            .then(refreshDocuments)
        e.target.value = ''
    }

    const onDelete = () => {
        dispatch(RpqsAction.deleteDocument(getRelativePath(documentToDelete.url))).then(() => {
            setDocumentToDelete(undefined)
            refreshDocuments()
        })
    }

    const rows = useMemo(() => orderBy(documents, 'updateDate', 'desc').map(doc => ({
        ...doc,
        preview: {
            value: PICTURE_REGEX.test(doc.name) && (
                <Box component='img' src={doc.url} sx={{ height: 40, maxWidth: 60, objectFit: 'cover' }} />
            ),
        },
        name: { value: <Link href={doc.url} target='_blank' rel='noopener noreferrer'>{doc.name}</Link> },
        managementEntity: { value: getManagementUnitName(doc.url, collectivityPath, managementUnits) },
        size: { value: `${formatThousands(Math.ceil(doc.size / 1024))} Ko` },
        updateDate: { value: getFullDate(doc.updateDate) },
    })), [documents, managementUnits, collectivityPath])

    return (
        <Grid container sx={{ padding: 1 }}>
            <Grid size={12}>
                <CardTable
                    title={i18n.picturesAndDocuments}
                    headers={getHeaders()}
                    rows={rows}
                    color={mainBlue}
                    rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                    actions={[{
                        icon: 'note_add',
                        color: 'white',
                        tooltip: i18n.importFile,
                        onClick: () => inputRef.current?.click(),
                    }]}
                    lineActions={[{
                        icon: 'delete',
                        onClick: row => setDocumentToDelete(row),
                    }]}
                />
                <input ref={inputRef} type='file' multiple hidden onChange={onUpload} />
            </Grid>
            <ConfirmModal
                isOpen={!isNil(documentToDelete)}
                title={i18n.sureDeleteRpqsDocument}
                onValidate={onDelete}
                onClose={() => setDocumentToDelete(undefined)}
            />
        </Grid>
    )
}

RpqsDocumentsPanel.propTypes = {
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    collectivityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default RpqsDocumentsPanel
